import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/data-handling")({
  head: () => ({
    meta: [
      { title: "Data Handling Policy, HOWDEN" },
      {
        name: "description",
        content:
          "How HOWDEN stores, secures, retains, and transfers customer data.",
      },
      { property: "og:title", content: "Data Handling Policy, HOWDEN" },
      {
        property: "og:description",
        content:
          "How HOWDEN stores, secures, retains, and transfers customer data.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/data-handling" },
    ],
    links: [{ rel: "canonical", href: "https://howdendiamonds.com/data-handling" }],
  }),
  component: DataHandlingPage,
});

function DataHandlingPage() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-20 lg:px-12 lg:py-32">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground">
        Legal
      </p>
      <h1 className="mt-6 font-serif text-5xl font-light leading-[1.05] lg:text-6xl">
        Data Handling
      </h1>
      <p className="mt-6 text-xs uppercase tracking-luxe text-muted-foreground">
        Last updated: May 2026
      </p>

      <div className="mt-16 space-y-12 text-base leading-relaxed text-muted-foreground">
        <section>
          <h2 className="font-serif text-2xl text-foreground">Where data lives</h2>
          <p className="mt-4">
            Customer accounts, orders, and email records are stored on managed,
            encrypted infrastructure provided by our backend (Lovable Cloud) and
            our e-commerce platform (Shopify). All data is encrypted in transit
            (TLS 1.2+) and at rest.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">
            Access controls
          </h2>
          <p className="mt-4">
            Only authorised HOWDEN personnel can access customer records, and
            only for legitimate operational reasons, fulfilling orders,
            answering enquiries, or resolving issues. Access is logged and
            reviewed.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">Retention</h2>
          <ul className="mt-4 list-disc space-y-2 pl-6">
            <li>
              <strong className="text-foreground">Account data:</strong> kept
              while your account is active. Deleted within 30 days of account
              closure.
            </li>
            <li>
              <strong className="text-foreground">Order records:</strong> kept
              for seven years for tax, warranty, and accounting compliance.
            </li>
            <li>
              <strong className="text-foreground">Marketing lists:</strong>{" "}
              removed immediately upon unsubscribe.
            </li>
            <li>
              <strong className="text-foreground">Support correspondence:</strong>{" "}
              kept for two years, then archived or deleted.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">
            International transfers
          </h2>
          <p className="mt-4">
            Some of our service providers operate outside your country. Where
            this happens we rely on standard contractual clauses or equivalent
            safeguards to ensure your data is protected to the same standard.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">Breach response</h2>
          <p className="mt-4">
            In the unlikely event of a data breach affecting your personal
            information, we will notify affected customers and the relevant
            supervisory authority within 72 hours of becoming aware, in line
            with GDPR and equivalent regulations.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">
            Requests &amp; deletion
          </h2>
          <p className="mt-4">
            To request export or deletion of your data, email{" "}
            <a
              href="mailto:hello@howden.diamonds"
              className="border-b border-hairline text-foreground hover:border-foreground"
            >
              hello@howden.diamonds
            </a>
            . We respond within 30 days.
          </p>
        </section>
      </div>
    </div>
  );
}
